import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createValentinePage } from '../lib/supabase';
import toast from 'react-hot-toast';

type PackageType = 'blush_of_love' | 'cupids_masterpiece';

interface FormData {
  sender: string;
  receiver: string;
  message: string;
  package_type: PackageType;
  spotify_title: string;
  spotify_artist: string;
  card_message?: string;
  viewPassword: string;
}

export default function CreateValentine() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    sender: '',
    receiver: '',
    message: '',
    package_type: 'blush_of_love',
    spotify_title: '',
    spotify_artist: '',
    viewPassword: '',
  });

  const image1Ref = useRef<HTMLInputElement>(null);
  const image2Ref = useRef<HTMLInputElement>(null);

  const isCupidsMasterpiece = formData.package_type === 'cupids_masterpiece';

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isSubmitting) {
      timeoutId = setTimeout(() => {
        setIsSubmitting(false);
      }, 5000);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isSubmitting]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) {
      toast.error(
        'Please wait while your previous submission is being processed.'
      );
      return;
    }
    setIsSubmitting(true);

    try {
      // Basic form validation
      if (formData.sender.trim().length < 2) {
        toast.error('Please enter a valid sender name (at least 2 characters)');
        setIsSubmitting(false);
        return;
      }

      if (formData.receiver.trim().length < 2) {
        toast.error(
          'Please enter a valid recipient name (at least 2 characters)'
        );
        setIsSubmitting(false);
        return;
      }

      if (formData.message.trim().length < 10) {
        toast.error(
          'Your message is too short. Please write at least 10 characters.'
        );
        setIsSubmitting(false);
        return;
      }

      if (formData.message.trim().length > 1000) {
        toast.error(
          'Your message is too long. Please keep it under 1000 characters.'
        );
        setIsSubmitting(false);
        return;
      }

      if (
        formData.spotify_title.trim().length < 1 ||
        formData.spotify_artist.trim().length < 1
      ) {
        toast.error('Please provide both the song title and artist name.');
        setIsSubmitting(false);
        return;
      }

      const image1 = image1Ref.current?.files?.[0];
      const image2 = isCupidsMasterpiece
        ? image2Ref.current?.files?.[0]
        : undefined;

      // Image validation
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (!image1) {
        toast.error('Please select an image for your Valentine.');
        setIsSubmitting(false);
        return;
      }

      if (image1.size > maxSize) {
        toast.error(
          'Image 1 must be smaller than 5MB. Try compressing it first.'
        );
        setIsSubmitting(false);
        return;
      }

      if (
        !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(
          image1.type
        )
      ) {
        toast.error('Image 1 must be a JPEG, PNG, GIF, or WebP file.');
        setIsSubmitting(false);
        return;
      }

      if (isCupidsMasterpiece) {
        if (!image2) {
          toast.error(
            "Please provide both images for Cupid's Masterpiece package"
          );
          setIsSubmitting(false);
          return;
        }

        if (image2.size > maxSize) {
          toast.error(
            'Image 2 must be smaller than 5MB. Try compressing it first.'
          );
          setIsSubmitting(false);
          return;
        }

        if (
          !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(
            image2.type
          )
        ) {
          toast.error('Image 2 must be a JPEG, PNG, GIF, or WebP file.');
          setIsSubmitting(false);
          return;
        }

        if (!formData.card_message?.trim()) {
          toast.error(
            "Please provide a card message for Cupid's Masterpiece package"
          );
          setIsSubmitting(false);
          return;
        }

        if (formData.card_message.trim().length > 500) {
          toast.error('Card message must be less than 500 characters.');
          setIsSubmitting(false);
          return;
        }
      }

      if (formData.viewPassword.length < 6) {
        toast.error('View password must be at least 6 characters long');
        setIsSubmitting(false);
        return;
      }

      await createValentinePage({
        ...formData,
        image1: image1,
        image2: image2,
      });

      toast.success(
        'Valentine page created! 💝 You will receive an email when it is approved. Please check your email for updates.',
        { duration: 6000 }
      );
      navigate('/my-valentines');
    } catch (error: any) {
      if (error?.message?.toLowerCase().includes('rate limit exceeded')) {
        toast.error(
          'You have created too many Valentine pages recently. Please try again later.',
          { duration: 5000 }
        );
      } else if (
        error?.message?.toLowerCase().includes('duplicate submission')
      ) {
        toast.error(
          'You recently created a similar Valentine. Please wait a few minutes before creating another.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('storage quota')) {
        toast.error(
          'Unable to upload images. Please try with smaller images.',
          { duration: 5000 }
        );
      } else if (error?.message?.toLowerCase().includes('not authenticated')) {
        toast.error('Your session has expired. Please sign in again.', {
          duration: 5000,
        });
        navigate('/login');
      } else if (error?.message?.toLowerCase().includes('network')) {
        toast.error(
          'Network error. Please check your internet connection and try again.',
          { duration: 5000 }
        );
      } else {
        toast.error('Failed to create Valentine page. Please try again.', {
          duration: 5000,
        });
      }
      console.error('Error:', error);
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="max-w-2xl w-full bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-center mb-8">
          Create Valentine Page
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="sender"
                className="block text-sm font-medium text-gray-700"
              >
                From
              </label>
              <input
                type="text"
                id="sender"
                value={formData.sender}
                onChange={(e) =>
                  setFormData({ ...formData, sender: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
                minLength={2}
                maxLength={50}
              />
            </div>
            <div>
              <label
                htmlFor="receiver"
                className="block text-sm font-medium text-gray-700"
              >
                To
              </label>
              <input
                type="text"
                id="receiver"
                value={formData.receiver}
                onChange={(e) =>
                  setFormData({ ...formData, receiver: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
                minLength={2}
                maxLength={50}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700"
            >
              Message
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
              minLength={10}
              maxLength={1000}
            />
            <p className="mt-1 text-sm text-gray-500">
              {formData.message.length}/1000 characters
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Package Type
            </label>
            <select
              value={formData.package_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  package_type: e.target.value as PackageType,
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            >
              <option value="blush_of_love">Blush of Love</option>
              <option value="cupids_masterpiece">Cupid's Masterpiece</option>
            </select>
          </div>
          {isCupidsMasterpiece && (
            <div>
              <label
                htmlFor="card_message"
                className="block text-sm font-medium text-gray-700"
              >
                Valentine Card Message
              </label>
              <textarea
                id="card_message"
                value={formData.card_message}
                onChange={(e) =>
                  setFormData({ ...formData, card_message: e.target.value })
                }
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
                maxLength={500}
              />
              <p className="mt-1 text-sm text-gray-500">
                {formData.card_message?.length || 0}/500 characters
              </p>
            </div>
          )}
          <div className={isCupidsMasterpiece ? 'grid grid-cols-2 gap-6' : ''}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {isCupidsMasterpiece ? 'Image 1' : 'Upload Image'}
              </label>
              <input
                type="file"
                ref={image1Ref}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="mt-1 block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-pink-50 file:text-pink-700
                hover:file:bg-pink-100"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Maximum size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
              </p>
            </div>
            {isCupidsMasterpiece && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Image 2
                </label>
                <input
                  type="file"
                  ref={image2Ref}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-pink-50 file:text-pink-700
                  hover:file:bg-pink-100"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Maximum size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="spotify_title"
                className="block text-sm font-medium text-gray-700"
              >
                Song Title
              </label>
              <input
                type="text"
                id="spotify_title"
                value={formData.spotify_title}
                onChange={(e) =>
                  setFormData({ ...formData, spotify_title: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label
                htmlFor="spotify_artist"
                className="block text-sm font-medium text-gray-700"
              >
                Artist
              </label>
              <input
                type="text"
                id="spotify_artist"
                value={formData.spotify_artist}
                onChange={(e) =>
                  setFormData({ ...formData, spotify_artist: e.target.value })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                required
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="viewPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Viewing Password
            </label>
            <input
              type="password"
              id="viewPassword"
              value={formData.viewPassword}
              onChange={(e) =>
                setFormData({ ...formData, viewPassword: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
              required
              minLength={6}
            />
            <p className="mt-1 text-sm text-gray-500">
              This password will be required to view the Valentine page
            </p>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Create Valentine Page'}
          </button>
        </form>
      </div>
    </div>
  );
}
